'use client';

import Link from 'next/link';
import dynamic from 'next/dynamic';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { AlertCircle, ArrowRight, Braces, Camera, Globe2, Play, Sparkles } from 'lucide-react';

import { ExtractForm } from '@/components/ExtractForm';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import {
  MarketingPageShell,
  PageHero,
  PageIntro,
  PageSection,
  SectionPill,
  SurfacePanel,
} from '@/components/site/page-shell';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  homepageFaqCopy,
  homepageFaqItems,
  homepageHeroCopy,
  homepageHowItWorksCopy,
  homepageUseCaseFlows,
  homepageUseCasesCopy,
} from '@/content/homepage';
import { useWebSocket } from '@/hooks/useWebSocket';
import { useAnimation } from '@/contexts/AnimationContext';
import { isScreenshotLoadingState, shouldShowExtractionStatusPanel } from '@/lib/extractionStatus';
import { fetchRunStatus, issueWebsocketToken, isTerminalExtractStatus, type ExtractRunSession } from '@/services/api';

const ResultDisplay = dynamic(
  () => import('@/components/ResultDisplay').then((module) => module.ResultDisplay),
  {
    ssr: false,
    loading: () => (
      <div className="p-6 text-sm text-[var(--site-ink-muted)]">
        Загружаем результат...
      </div>
    ),
  },
);

interface ScraperAppProps {
  turnstileSiteKey?: string | null;
  turnstileBypass?: boolean;
}

const heroFlow = homepageUseCaseFlows[0];
const heroSurfaceClassName = 'w-full !px-4 sm:!px-6 lg:!px-0';
const heroFieldPreview = heroFlow.fields.slice(0, 3);
const workflowSteps = [
  {
    title: 'Укажите адрес страницы',
    description: 'Начните с карточки товара, каталога или поисковой выдачи и задайте нужные поля.',
    Icon: Globe2,
  },
  {
    title: 'Сохраните проверочный скриншот',
    description: 'В одном запуске видно, как выглядела страница в момент извлечения.',
    Icon: Camera,
  },
  {
    title: 'Получите JSON и готовые поля',
    description: 'Результат сразу подходит для шаблонов, датасетов и API-сценариев.',
    Icon: Braces,
  },
];

export function ScraperApp({ turnstileSiteKey, turnstileBypass }: ScraperAppProps) {
  const [runSession, setRunSession] = useState<ExtractRunSession | null>(null);
  const [isPending, setIsPending] = useState(false);
  const { isLoading, screenshot_url, json, status, messages, error } = useWebSocket(runSession);
  const { enableAnimations } = useAnimation();
  const [isMounted, setIsMounted] = useState(false);
  const [extractionComplete, setExtractionComplete] = useState(true);
  const [manualError, setManualError] = useState<string | null>(null);
  const [waitingForResults, setWaitingForResults] = useState(false);
  const [currentRunQuery, setCurrentRunQuery] = useState<string | null>(null);

  const updateRunQuery = useCallback((runId: string | null) => {
    if (typeof window === 'undefined') {
      return;
    }

    const nextUrl = new URL(window.location.href);
    if (runId) {
      nextUrl.searchParams.set('run', runId);
    } else {
      nextUrl.searchParams.delete('run');
    }

    const currentUrl = new URL(window.location.href);
    if (nextUrl.search === currentUrl.search) {
      setCurrentRunQuery(nextUrl.searchParams.get('run'));
      return;
    }

    window.history.replaceState(null, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
    setCurrentRunQuery(nextUrl.searchParams.get('run'));
  }, []);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (runSession) {
      setExtractionComplete(false);
    }
  }, [runSession]);

  useEffect(() => {
    if (json && !isLoading) {
      setExtractionComplete(true);
    }
  }, [json, isLoading]);

  useEffect(() => {
    if (!isLoading && status === 'closed') {
      setExtractionComplete(true);
    }
  }, [isLoading, status]);

  const handleExtractFailed = useCallback((message?: string) => {
    setIsPending(false);
    setExtractionComplete(true);
    if (message) {
      setManualError(message);
    }
  }, []);

  useEffect(() => {
    if (error) {
      handleExtractFailed();
    }
  }, [error, handleExtractFailed]);

  const handleExtractStart = useCallback((newRun: ExtractRunSession | null) => {
    if (newRun === null) {
      setRunSession(null);
      setIsPending(true);
      setExtractionComplete(false);
      setManualError(null);
      setWaitingForResults(true);
      updateRunQuery(null);
      return;
    }

    setRunSession(null);
    setManualError(null);

    setTimeout(() => {
      setRunSession(newRun);
      setIsPending(false);
      updateRunQuery(newRun.runId);
    }, 50);
  }, [updateRunQuery]);

  useEffect(() => {
    if (!isMounted || typeof window === 'undefined') {
      return;
    }

    const syncRunQuery = () => {
      const params = new URLSearchParams(window.location.search);
      setCurrentRunQuery(params.get('run'));
    };

    syncRunQuery();
    window.addEventListener('popstate', syncRunQuery);
    return () => {
      window.removeEventListener('popstate', syncRunQuery);
    };
  }, [isMounted]);

  useEffect(() => {
    if (!isMounted) {
      return;
    }

    const runId = currentRunQuery;
    if (!runId || runSession?.runId === runId) {
      return;
    }

    let isCurrent = true;
    setManualError(null);
    setWaitingForResults(true);
    setIsPending(false);

    const rehydrate = async () => {
      try {
        let nextRun = await fetchRunStatus(runId);
        if (isCurrent && !isTerminalExtractStatus(nextRun.status)) {
          const token = await issueWebsocketToken(runId);
          nextRun = { ...nextRun, ...token };
        }

        if (!isCurrent) {
          return;
        }

        setRunSession(nextRun);
        setWaitingForResults(!isTerminalExtractStatus(nextRun.status) && !nextRun.json && !nextRun.error);
      } catch (rehydrationError) {
        if (!isCurrent) {
          return;
        }
        const message =
          rehydrationError instanceof Error
            ? rehydrationError.message
            : 'Не удалось восстановить состояние запуска';
        setRunSession(null);
        setManualError(message);
        setWaitingForResults(false);
        updateRunQuery(null);
      }
    };

    void rehydrate();
    return () => {
      isCurrent = false;
    };
  }, [currentRunQuery, isMounted, runSession?.runId, updateRunQuery]);

  const isProcessing = isLoading || isPending || status === 'connecting';
  const shouldShowStatusPanel = shouldShowExtractionStatusPanel(status, isPending, error, manualError);
  const isScreenshotLoading = isScreenshotLoadingState(isProcessing, screenshot_url);

  useEffect(() => {
    if (json || error || manualError || (!isProcessing && status === 'closed')) {
      setWaitingForResults(false);
    }
  }, [json, error, manualError, isProcessing, status]);

  const renderError = useMemo(() => {
    const activeError = error ?? manualError;
    if (activeError == null) {
      return null;
    }

    const normalizedError =
      activeError.trim() || 'Мы не смогли обработать запрос на извлечение. Попробуйте позже.';

    return (
      <Alert variant="destructive" data-testid="extraction-error">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{normalizedError}</AlertDescription>
      </Alert>
    );
  }, [error, manualError]);

  return (
    <ErrorBoundary>
      <MarketingPageShell
        contentClassName="gap-6 xl:gap-8"
        hero={
          <PageHero variant="plain" surfaceClassName={heroSurfaceClassName}>
            <div className="grid gap-6 xl:grid-cols-[minmax(0,0.88fr)_minmax(24rem,1.12fr)] xl:items-start xl:[gap:var(--site-home-hero-grid-gap)] 2xl:grid-cols-[minmax(0,0.9fr)_minmax(26rem,1.1fr)] 2xl:gap-10">
              <PageIntro
                className="w-full gap-5 xl:max-w-[var(--site-home-hero-intro-max)] xl:[gap:var(--site-home-hero-intro-gap)]"
                eyebrow={
                  <>
                    <Sparkles className="h-3.5 w-3.5" />
                    {homepageHeroCopy.eyebrow}
                  </>
                }
                title={
                  <span
                    className="block max-w-[11.5ch] whitespace-pre-line leading-[0.96] tracking-[-0.05em] [font-size:clamp(2.3rem,3.35vw,3.95rem)] min-[1280px]:max-[1440px]:[font-size:clamp(1.84rem,2.68vw,3.16rem)]"
                  >
                    {homepageHeroCopy.title}
                  </span>
                }
                description={
                  <span className="block max-w-[30rem] text-base leading-7">
                    {homepageHeroCopy.description}
                  </span>
                }
                actions={
                  <>
                    <Link href="#live-extraction">
                      <Button>
                        Запустить извлечение
                        <Play className="h-4 w-4 fill-current" />
                      </Button>
                    </Link>
                    <Link href="/templates">
                      <Button variant="outline">
                        Открыть шаблоны
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </Link>
                  </>
                }
              >
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm font-medium text-[var(--site-ink-muted)] max-sm:text-[0.8125rem]">
                  {homepageHeroCopy.benefits.map((benefit, index) => (
                    <span key={benefit} className="inline-flex items-center gap-3">
                      {index > 0 ? <span className="text-[var(--site-border-strong)]">•</span> : null}
                      <span>{benefit}</span>
                    </span>
                  ))}
                </div>
              </PageIntro>

              <div id="live-extraction">
                <SurfacePanel className="gap-5 border-[color:rgba(255,255,255,0.62)] bg-[linear-gradient(180deg,rgba(255,255,255,0.94),rgba(255,255,255,0.84))] p-[var(--site-home-hero-panel-pad)] shadow-[0_28px_80px_rgba(23,38,59,0.1)] max-sm:gap-4">
                  <div className="space-y-3 max-sm:space-y-2.5">
                    <div className="inline-flex w-fit items-center gap-2 rounded-full border border-[color:rgba(23,38,59,0.08)] bg-white/76 px-[var(--site-home-hero-flow-pad-x)] py-[var(--site-home-hero-flow-pad-y)] text-[length:var(--site-home-hero-flow-font-size)] font-semibold uppercase tracking-[0.16em] text-[var(--site-ink-strong)]">
                      <Play className="h-[var(--site-home-hero-flow-icon-size)] w-[var(--site-home-hero-flow-icon-size)] fill-current" />
                      Живое извлечение
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-[length:var(--site-home-hero-flow-font-size)] font-semibold uppercase tracking-[0.16em] text-[var(--site-ink-subtle)] max-sm:gap-1.5">
                      <span className="rounded-full border border-[color:rgba(23,38,59,0.08)] bg-white/82 px-[var(--site-home-hero-flow-pad-x)] py-[var(--site-home-hero-flow-pad-y)]">
                        URL страницы
                      </span>
                      <ArrowRight className="hidden h-[var(--site-home-hero-flow-icon-size)] w-[var(--site-home-hero-flow-icon-size)] sm:block" />
                      <span className="rounded-full border border-[color:rgba(23,38,59,0.08)] bg-white/82 px-[var(--site-home-hero-flow-pad-x)] py-[var(--site-home-hero-flow-pad-y)]">
                        Скриншот
                      </span>
                      <ArrowRight className="hidden h-[var(--site-home-hero-flow-icon-size)] w-[var(--site-home-hero-flow-icon-size)] sm:block" />
                      <span className="hidden rounded-full border border-[color:rgba(23,38,59,0.08)] bg-white/82 px-[var(--site-home-hero-flow-pad-x)] py-[var(--site-home-hero-flow-pad-y)] sm:inline-flex">
                        Поля / JSON
                      </span>
                    </div>
                    <p className="max-w-2xl text-sm leading-6 text-[var(--site-ink-muted)]">
                      Один запуск сохраняет источник, проверочный скриншот и структурированные веб-данные для шаблонов,
                      датасетов и API.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {heroFieldPreview.map((field, index) => (
                        <SectionPill
                          key={field}
                          className={`${index > 1 ? 'hidden sm:inline-flex ' : ''}bg-white/82 px-[var(--site-home-hero-chip-pad-x)] py-[var(--site-home-hero-chip-pad-y)] text-[length:var(--site-home-hero-chip-font-size)]`}
                        >
                          {field}
                        </SectionPill>
                      ))}
                    </div>
                  </div>

                  <ExtractForm
                    onExtractStart={handleExtractStart}
                    onExtractFailed={handleExtractFailed}
                    isLoading={isProcessing}
                    extractionComplete={extractionComplete}
                    turnstileSiteKeyProp={turnstileSiteKey ?? null}
                    turnstileBypassProp={turnstileBypass}
                  />

                  {renderError}

                  {waitingForResults && !error && !manualError ? (
                    <div className="flex items-center gap-2 rounded-[1.5rem] border border-[color:var(--site-accent-border)] bg-[var(--site-accent-soft)] px-4 py-3 text-sm text-[var(--site-accent-text)] transition-opacity duration-300">
                      <AlertCircle className="h-4 w-4" />
                      <span>Ждем результаты извлечения...</span>
                    </div>
                  ) : null}

                  {shouldShowStatusPanel ? (
                    <SurfacePanel
                      tone="muted"
                      className="items-center text-center"
                      data-testid="extraction-status"
                    >
                      <div className={`${enableAnimations ? 'animate-pulse' : ''} flex flex-col items-center gap-3`}>
                        <div className="h-12 w-12 rounded-full bg-[color:rgba(23,38,59,0.08)]" />
                        <p className="font-medium text-[var(--site-ink-strong)]">
                          {isPending ? 'Запускаем процесс извлечения...' : 'Обрабатываем ваш запрос...'}
                        </p>
                        <p className="max-w-md text-sm text-[var(--site-ink-muted)]">
                          Это может занять немного времени в зависимости от сайта и структуры извлекаемых данных.
                        </p>
                      </div>
                    </SurfacePanel>
                  ) : null}
                </SurfacePanel>
              </div>
            </div>
          </PageHero>
        }
      >
        {(screenshot_url || json || isProcessing) ? (
          <PageSection className="gap-6">
            <div className="flex flex-col gap-2">
              <h2 className="text-2xl font-semibold tracking-[-0.03em] text-[var(--site-ink-strong)]">
                Результат запуска
              </h2>
              <p className="text-sm leading-6 text-[var(--site-ink-muted)]">
                Скриншот страницы и структурированный результат остаются рядом, чтобы ускорить проверку.
              </p>
            </div>

            <div className="grid gap-6 lg:grid-cols-[0.95fr_2.05fr]">
              <SurfacePanel className="hidden overflow-hidden p-0 lg:block">
                <ResultDisplay
                  screenshotUrl={screenshot_url}
                  jsonData={null}
                  isLoading={isScreenshotLoading}
                  displayMode="screenshot"
                />
              </SurfacePanel>

              <SurfacePanel className="overflow-hidden p-0" data-testid="extraction-results">
                <ResultDisplay
                  screenshotUrl={null}
                  jsonData={json}
                  isLoading={isProcessing && !json}
                  displayMode="data"
                />
              </SurfacePanel>
            </div>
          </PageSection>
        ) : null}

        <PageSection mobileStyle="band" className="gap-6">
          <PageIntro
            align="center"
            compact
            headingLevel="h2"
            className="mx-auto max-w-[48rem]"
            eyebrow={homepageHowItWorksCopy.eyebrow}
            title={homepageHowItWorksCopy.title}
            description={homepageHowItWorksCopy.description}
          />
          <div className="grid gap-4 max-sm:gap-0 max-sm:divide-y max-sm:divide-[color:var(--site-border-soft)] lg:grid-cols-3">
            {workflowSteps.map(({ title, description, Icon }, index) => (
              <SurfacePanel
                key={title}
                phoneStyle="open"
                className="gap-4 p-5 max-sm:flex-row max-sm:items-start max-sm:gap-4 max-sm:py-4"
              >
                <div className="flex items-center justify-between gap-4 max-sm:min-w-[4.5rem] max-sm:flex-col max-sm:items-start">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[var(--site-surface-deep)] text-[var(--site-ink-strong)]">
                    <Icon className="h-4.5 w-4.5" />
                  </div>
                  <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--site-ink-subtle)]">
                    Шаг {index + 1}
                  </span>
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-[var(--site-ink-strong)]">{title}</h3>
                  <p className="text-sm leading-6 text-[var(--site-ink-muted)]">{description}</p>
                </div>
              </SurfacePanel>
            ))}
          </div>
        </PageSection>

        <PageSection mobileStyle="band" className="gap-6">
          <PageIntro
            align="center"
            compact
            headingLevel="h2"
            className="mx-auto max-w-[48rem]"
            eyebrow={homepageUseCasesCopy.eyebrow}
            title={homepageUseCasesCopy.title}
            description={homepageUseCasesCopy.description}
          />
          <div className="grid gap-4 max-sm:gap-0 max-sm:divide-y max-sm:divide-[color:var(--site-border-soft)] xl:grid-cols-3">
            {homepageUseCaseFlows.map((flow) => (
              <SurfacePanel
                key={flow.title}
                phoneStyle="open"
                className="gap-4 p-5 max-sm:gap-3 max-sm:py-4"
              >
                <div className="space-y-3">
                  <SectionPill>{flow.badge}</SectionPill>
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-[var(--site-ink-strong)]">{flow.title}</h3>
                    <p className="text-sm leading-6 text-[var(--site-ink-muted)]">{flow.description}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {flow.fields.slice(0, 4).map((field) => (
                    <span
                      key={field}
                      className="rounded-full border border-[color:var(--site-border-soft)] bg-[var(--site-surface-deep)] px-3 py-1 text-xs text-[var(--site-ink)]"
                    >
                      {field}
                    </span>
                  ))}
                </div>
                <div className="hidden text-sm text-[var(--site-ink-muted)] sm:block">{flow.stats.join(' • ')}</div>
              </SurfacePanel>
            ))}
          </div>
          <div className="flex justify-center">
            <Link href="/templates">
              <Button variant="outline">
                Открыть шаблоны
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </PageSection>

        <PageSection mobileStyle="band" className="gap-6">
          <PageIntro
            align="center"
            compact
            headingLevel="h2"
            className="mx-auto max-w-[52rem]"
            eyebrow={homepageFaqCopy.eyebrow}
            title={homepageFaqCopy.title}
            description={homepageFaqCopy.description}
          />
          <div className="grid gap-4 max-sm:gap-0 max-sm:divide-y max-sm:divide-[color:var(--site-border-soft)] lg:grid-cols-2">
            {homepageFaqItems.map((item) => (
              <SurfacePanel key={item.question} phoneStyle="open" className="gap-3 p-5 max-sm:py-4">
                <h3 className="text-lg font-semibold text-[var(--site-ink-strong)]">{item.question}</h3>
                <p className="text-sm leading-6 text-[var(--site-ink-muted)]">{item.answer}</p>
              </SurfacePanel>
            ))}
          </div>
          <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm font-medium text-[var(--site-ink-muted)]">
            <Link
              href="/api-documentation"
              className="inline-flex min-h-11 items-center text-[var(--site-ink-strong)] underline-offset-4 hover:underline"
            >
              API документация
            </Link>
            <Link
              href="/templates"
              className="inline-flex min-h-11 items-center text-[var(--site-ink-strong)] underline-offset-4 hover:underline"
            >
              Каталог шаблонов
            </Link>
            <Link
              href="/datasets"
              className="inline-flex min-h-11 items-center text-[var(--site-ink-strong)] underline-offset-4 hover:underline"
            >
              Датасеты
            </Link>
          </div>
        </PageSection>
      </MarketingPageShell>
    </ErrorBoundary>
  );
}
